import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ApiError, post } from "../../api/client";
import { Button } from "../../components/ui/Button";
import { PasswordField } from "../../components/ui/Field";
import { InlineError, Loading } from "../../components/ui/misc";
import { AuthLayout } from "./AuthLayout";

/** Token arrives in the URL fragment (#token=…) so it is never sent to servers or logged in Referer headers. */
function readToken(): string {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  return hash.get("token") ?? "";
}

export function ResetPasswordPage({ mode }: { mode: "reset" | "invite" }) {
  const [token] = useState(readToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const check = useQuery({
    queryKey: ["token-inspect", token],
    queryFn: () => post<{ valid: boolean; purpose?: string; name?: string }>("/auth/password/token/inspect", { token }),
    enabled: token.length >= 20,
    retry: false,
  });

  const title = mode === "invite" ? "Activate your account" : "Choose a new password";
  if (!token || token.length < 20) {
    return <AuthLayout title={title}><p className="text-sm text-default">This link is incomplete. Open the link from your email again.</p></AuthLayout>;
  }
  if (check.isLoading) return <AuthLayout title={title}><Loading /></AuthLayout>;
  if (check.error) {
    // A failed check is not the same as an invalid link: say what actually went wrong.
    const limited = check.error instanceof ApiError && check.error.status === 429;
    return (
      <AuthLayout title={title}>
        <p role="alert" className="text-sm text-default">
          {limited
            ? "Too many attempts from this network. Wait a few minutes, then reload this page — your link is still usable until it expires."
            : "We couldn't check this link right now. Make sure you're connected, then try again."}
        </p>
        <Button className="mt-4" onClick={() => void check.refetch()} loading={check.isFetching}>Try again</Button>
      </AuthLayout>
    );
  }
  if (!check.data?.valid) {
    return (
      <AuthLayout title={title}>
        <p className="text-sm text-default">This link is invalid, has expired, or was already used. {mode === "invite" ? "Invitation links last 7 days." : "Reset links last 60 minutes, and requesting a new one cancels older links."}</p>
        <p className="mt-3 text-sm"><Link className="text-accent-600 hover:underline" to="/forgot-password">Request a new link</Link></p>
      </AuthLayout>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError({ message: "Passwords do not match" });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await post("/auth/password/reset", { token, password });
      window.history.replaceState(null, "", window.location.pathname);
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={title} subtitle={check.data.name ? `Welcome, ${check.data.name}.` : undefined}>
      {done ? (
        <div className="space-y-3">
          <p role="status" className="text-sm text-default">Your password is set. All previous sessions were signed out.</p>
          <Link className="text-sm text-accent-600 hover:underline" to="/login">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <PasswordField label="New password" autoComplete="new-password" minLength={12} maxLength={128} value={password}
            onChange={(e) => setPassword(e.target.value)} required hint="At least 12 characters. Can't be one of your last 5 passwords or contain your name, email, organization name or phone numbers." autoFocus />
          <PasswordField label="Confirm password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          <InlineError error={error} />
          <Button type="submit" variant="primary" className="w-full" loading={busy}>{mode === "invite" ? "Activate account" : "Set password"}</Button>
        </form>
      )}
    </AuthLayout>
  );
}
